import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, X, Gift } from "lucide-react";

// Routes where the floating CTA / exit modal would be noise.
const HIDE_ON = ["/submit", "/submitted", "/signin", "/signup"];

export default function StickyCTA() {
  const { pathname } = useLocation();
  const [showExit, setShowExit] = useState(false);
  const hidden = HIDE_ON.some((p) => pathname.startsWith(p)) || pathname.startsWith("/admin");

  // Desktop exit-intent: when the mouse leaves through the top of the viewport,
  // show a one-time (per session) offer modal.
  useEffect(() => {
    if (hidden) return undefined;
    if (sessionStorage.getItem("gc_exit_shown")) return undefined;
    const onLeave = (e) => {
      if (e.clientY <= 0) {
        setShowExit(true);
        sessionStorage.setItem("gc_exit_shown", "1");
        document.removeEventListener("mouseout", onLeave);
      }
    };
    const t = setTimeout(() => document.addEventListener("mouseout", onLeave), 4000);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseout", onLeave);
    };
  }, [hidden, pathname]);

  if (hidden) return null;

  return (
    <>
      {/* Sticky mobile bar */}
      <div className="gc-sticky-cta" data-testid="sticky-cta">
        <div className="gc-sticky-inner">
          <div className="gc-sticky-copy">
            <span className="gc-sticky-title">Got a gift card?</span>
            <span className="gc-sticky-sub">Get an estimate in 60s</span>
          </div>
          <Link to="/submit" data-testid="sticky-cta-btn" className="gc-sticky-btn">
            Sell a card <ArrowRight style={{ width: 16, height: 16 }} />
          </Link>
        </div>
      </div>

      {/* Desktop exit-intent modal */}
      {showExit && (
        <div className="gc-exit-overlay" data-testid="exit-intent-modal" onClick={() => setShowExit(false)}>
          <div className="gc-exit-card" onClick={(e) => e.stopPropagation()}>
            <button
              className="gc-exit-close"
              onClick={() => setShowExit(false)}
              data-testid="exit-intent-close"
              aria-label="Close"
            >
              <X style={{ width: 18, height: 18 }} />
            </button>
            <span className="gc-exit-ic"><Gift style={{ width: 22, height: 22 }} /></span>
            <h3 className="gc-exit-title">Before you go — get your estimate</h3>
            <p className="gc-exit-sub">
              See exactly what your gift card is worth in under a minute. No signup, no obligation —
              and validated cards are typically paid within 24 hours of approval.
            </p>
            <div className="gc-exit-actions">
              <Link to="/submit" data-testid="exit-intent-cta" className="gc-exit-primary" onClick={() => setShowExit(false)}>
                Get my quote <ArrowRight style={{ width: 16, height: 16 }} />
              </Link>
              <Link to="/rates" className="gc-exit-secondary" onClick={() => setShowExit(false)}>
                See rates first
              </Link>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .gc-sticky-cta {
          position: fixed; left: 0; right: 0; bottom: 0; z-index: 60;
          padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
          background: rgba(255,255,255,.85); backdrop-filter: blur(14px);
          border-top: 1px solid #eaf0ff; display: none;
        }
        .gc-sticky-inner { display:flex; align-items:center; justify-content:space-between; gap:12px; max-width:560px; margin:0 auto; }
        .gc-sticky-copy { display:flex; flex-direction:column; line-height:1.15; }
        .gc-sticky-title { font-family:"Hanken Grotesk",sans-serif; font-weight:800; color:#0b0d17; font-size:15px; }
        .gc-sticky-sub { font-size:12px; color:#6c6780; }
        .gc-sticky-btn {
          display:inline-flex; align-items:center; gap:6px; white-space:nowrap;
          padding:11px 18px; border-radius:90px; text-decoration:none;
          background:linear-gradient(120deg,#346af8 0%, #2554d6 100%); color:#ffffff !important;
          font-weight:800; font-size:14px; box-shadow:0 10px 22px -12px rgba(52,106,248,.6);
        }
        .gc-sticky-btn svg { color:#fff !important; }
        @media (max-width: 1023px){ .gc-sticky-cta { display:block; } }

        .gc-exit-overlay {
          position: fixed; inset: 0; z-index: 120; display:flex; align-items:center; justify-content:center;
          padding: 20px; background: rgba(28,24,40,.55); backdrop-filter: blur(4px);
          animation: gcFade .2s ease;
        }
        .gc-exit-card {
          position: relative; width: 100%; max-width: 460px; background:#fff; border-radius: 24px;
          padding: 32px 30px; box-shadow: 0 40px 80px -30px rgba(28,24,40,.6);
          animation: gcPop .25s cubic-bezier(.2,.7,.2,1);
        }
        .gc-exit-close { position:absolute; top:14px; right:14px; width:34px; height:34px; border-radius:50%;
          border:none; background:#f2f1fb; color:#6c6780; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; }
        .gc-exit-close:hover { background:#e6e4f6; color:#0b0d17; }
        .gc-exit-ic { display:inline-flex; width:48px; height:48px; border-radius:14px; align-items:center; justify-content:center;
          background:linear-gradient(135deg,#346af8,#2554d6); color:#ffffff !important; margin-bottom:14px; box-shadow:0 12px 24px -12px rgba(52,106,248,.7); }
        .gc-exit-ic svg { color:#fff !important; }
        .gc-exit-title { font-family:"Hanken Grotesk",sans-serif; font-weight:800; color:#0b0d17; font-size:23px; line-height:1.2; margin:0; }
        .gc-exit-sub { color:#6c6780; font-size:14.5px; line-height:1.6; margin:10px 0 22px; }
        .gc-exit-actions { display:flex; flex-wrap:wrap; gap:10px; }
        .gc-exit-primary { display:inline-flex; align-items:center; gap:7px; padding:12px 22px; border-radius:90px; text-decoration:none;
          background:linear-gradient(120deg,#346af8 0%, #2554d6 100%); color:#ffffff !important; font-weight:800; font-size:15px;
          box-shadow: 0 12px 26px -12px rgba(52,106,248,.6); }
        .gc-exit-primary:hover { filter: brightness(1.05); }
        .gc-exit-primary svg { color:#fff !important; }
        .gc-exit-secondary { display:inline-flex; align-items:center; padding:12px 20px; border-radius:90px; text-decoration:none;
          border:1px solid #e2e0f2; color:#0b0d17 !important; font-weight:700; font-size:15px; background:#fff; }
        @keyframes gcFade { from{opacity:0} to{opacity:1} }
        @keyframes gcPop { from{opacity:0; transform:translateY(10px) scale(.97)} to{opacity:1; transform:none} }
      `}</style>
    </>
  );
}
